import { useEffect, useState, useCallback } from "react";
import { API } from "@/App";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Link2, Copy, RefreshCw, MessageCircle } from "lucide-react";
import { WhatsAppGroupTexts } from "./WhatsAppGroupTexts";

export const PublicBookingSettings = ({ businessId }) => {
  const [s, setS] = useState(null);
  const [hours, setHours] = useState("3");

  const load = useCallback(async () => {
    const res = await fetch(`${API}/businesses/${businessId}/public-booking/settings`, { credentials: "include" });
    if (res.ok) {
      const d = await res.json();
      setS(d);
      setHours(String(d.cancel_hours ?? 3));
    }
  }, [businessId]);

  useEffect(() => { load(); }, [load]);

  const save = async (patch) => {
    const res = await fetch(`${API}/businesses/${businessId}/public-booking/settings`, {
      method: "PUT", credentials: "include", headers: { "Content-Type": "application/json" }, body: JSON.stringify(patch),
    });
    const d = await res.json();
    if (!res.ok) return toast.error(d.detail || "Kaydedilemedi");
    setS(d);
    toast.success("Rezervasyon linki ayarları kaydedildi");
  };

  if (!s) return null;
  const link = s.token ? `${window.location.origin}/rezervasyon/${s.token}` : "";
  const wa = `https://wa.me/?text=${encodeURIComponent(`Derslerimize buradan yer ayırtabilirsiniz: ${link}`)}`;

  return (
    <Card className="border-0 shadow-sm" data-testid="public-booking-settings">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-navy"><Link2 className="w-5 h-5 text-emerald-600" /> Herkese Açık Rezervasyon Linki</CardTitle>
        <CardDescription>Uygulamaya kayıtlı olmayan kişiler (örn. MultiSport ile gelenler), açtığınız derslere (grup, düet, özel ders) bu linkten giriş yapmadan yer ayırtabilir.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center justify-between rounded-xl border p-3">
          <span className="font-medium text-navy">Linki aktif et</span>
          <Switch checked={s.enabled} onCheckedChange={(v) => save({ enabled: v })} data-testid="public-booking-enabled-switch" />
        </div>
        {s.enabled && (
          <>
            <div className="flex flex-col sm:flex-row gap-2">
              <Input readOnly value={link} className="rounded-xl font-mono text-xs" data-testid="public-booking-link" />
              <Button variant="outline" className="rounded-xl" onClick={() => { navigator.clipboard.writeText(link).catch(() => {}); toast.success("Link kopyalandı"); }} data-testid="public-booking-copy-btn"><Copy className="w-4 h-4 mr-1" /> Kopyala</Button>
              <a href={wa} target="_blank" rel="noreferrer"><Button className="rounded-xl bg-emerald-600 hover:bg-emerald-700 w-full" data-testid="public-booking-whatsapp-btn"><MessageCircle className="w-4 h-4 mr-1" /> WhatsApp</Button></a>
            </div>
            <div className="flex items-end gap-2">
              <div>
                <Label>Linkten iptal: dersten en geç kaç saat önce?</Label>
                <Input type="number" min="0" max="168" value={hours} onChange={(e) => setHours(e.target.value)} className="rounded-xl w-32" data-testid="public-booking-cancel-hours" />
              </div>
              <Button variant="outline" className="rounded-xl" onClick={() => save({ cancel_hours: Number(hours) })} data-testid="public-booking-save-hours">Kaydet</Button>
            </div>
            <Button variant="ghost" className="text-red-600 rounded-xl px-0 hover:bg-transparent" onClick={() => window.confirm("Eski link çalışmaz hale gelecek. Yeni link oluşturulsun mu?") && save({ regenerate_token: true })} data-testid="public-booking-regenerate-btn">
              <RefreshCw className="w-4 h-4 mr-1" /> Linki yenile (eski link iptal olur)
            </Button>
            <p className="text-xs text-grey">Linkte sadece Rezervasyonlar &gt; "Online Ders Programı" bölümünden açtığınız dersler görünür.</p>
            <WhatsAppGroupTexts s={s} link={link} onSave={save} />
          </>
        )}
      </CardContent>
    </Card>
  );
};
